import { useFormik } from 'formik';
import { Fragment, useEffect, useRef } from 'react';

import { ImagesApp } from '../../constants/images';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useMembershipContext } from '../../hooks/contexts/membership.context';
import { useModalContext } from '../../hooks/contexts/modal.context';
import { useUserContext } from '../../hooks/contexts/user.context';
import maskValue from '../../utils/maskValue';
import LoaderMember from './Loader';
import * as Styles from './styles.identify-member';

const initialValues = { document : '' };

type IdentifyMemberProps = {
  size?: string
}

const IdentifyMember = ({ size }: IdentifyMemberProps) => {
  const { translate } = useI18nContext();
  const {
    removePassKeyApplied,
    event,
    producerId,
    changeEventLoading,
    updateTicketsWithMembership,
  } = useEventContext();
  const { toggleModal, setContentModal } = useModalContext();

  const { user, hasSsoAuth, setSsoAuth } = useUserContext();
  const {
    fetchingMembership,
    membershipError,
    changeMembershipError,
    membershipApplied,
    verifyMembership,
    removeMembership,
  } = useMembershipContext();

  const getTicket = async (document: string) => {
    changeEventLoading(true);
    const {
      hasMembership,
      passkey,
      membership,
    } = await verifyMembership(document, producerId);

    if (!hasMembership) {
      changeEventLoading(false);
      return;
    }

    await updateTicketsWithMembership(
      membership,
      passkey,
      document,
      toggleModal,
      setContentModal,
    );
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize : true,
    onSubmit : ({ document }) => getTicket(document),
  });

  const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    formik.setFieldValue('document', maskValue(value));
  };

  const handleRemoveMembership = async () => {
    await removePassKeyApplied();
    removeMembership();
  };

  const containsError = formik.errors.document && formik.touched.document;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user && user?.document?.number && hasSsoAuth && event?.producerId) {
      setSsoAuth(false);
      formik.setFieldValue('document', maskValue(user.document.number));
      getTicket(user.document.number);
    }
  }, [hasSsoAuth, user, event]);

  useEffect(() => {
    if (membershipApplied) {
      formik.setFieldValue('document', maskValue(membershipApplied.document));
    }

    return () => {
      if (containsError || membershipError || !membershipApplied) {
        changeMembershipError('');
        formik.resetForm();
      }
    };
  }, [membershipApplied]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [containsError, membershipError, membershipApplied]);

  return (
    <Styles.Content data-testid="identify-member-content" id="identify-member-content" $size={size}>
      {fetchingMembership && (<LoaderMember />)}

      {!fetchingMembership && (
        <Fragment>

          {(!membershipError && membershipApplied) && (
            <Styles.Form>
              <Styles.MemberContainer>
                <Styles.InputLabel>
                  CPF Sócio Torcedor
                </Styles.InputLabel>

                <Styles.InputContainer $membershipApplied>
                  <Styles.InputForm
                    $membershipApplied
                    id="input-document"
                    data-testid="input-document-memberapplied"
                    ref={inputRef}
                    name="document"
                    disabled={true}
                    value={maskValue(membershipApplied.document)}
                    onChange={handleDocumentChange}
                    type="text"
                    placeholder="___.___.___-__"
                  />

                  <Styles.RemoveMemberButton
                    onClick={handleRemoveMembership}
                    data-testid="remove-member-button"
                    id="remove-member-button"
                  >
                    <img src={ImagesApp.svg.close} alt="remove" />
                  </Styles.RemoveMemberButton>
                </Styles.InputContainer>
              </Styles.MemberContainer>
            </Styles.Form>
          )}

          {(!membershipApplied || membershipError) && (
            <Styles.Form
              onSubmit={formik.handleSubmit}
              onBlur={formik.handleBlur}
              autoComplete="off"
              id="form-apply-membership"
              data-testid="form-apply-membership"
            >
              <Styles.MemberContainer>
                <Styles.InputLabel>
                  CPF Sócio Torcedor
                </Styles.InputLabel>

                <Styles.InputContainer $containsError={Boolean(containsError)}>
                  <Styles.InputForm
                    id="input-document"
                    data-testid="input-document"
                    ref={inputRef}
                    name="document"
                    disabled={false}
                    $containsError={Boolean(containsError)}
                    value={formik.values.document}
                    onChange={handleDocumentChange}
                    type="text"
                    placeholder="___.___.___-__"
                  />
                </Styles.InputContainer>

                <Styles.ButtonForm type="submit" data-testid="button-apply-membership" id="button-apply-membership">
                  {translate(['INFORMATION', 'TO_APPLY'])}
                </Styles.ButtonForm>
              </Styles.MemberContainer>
            </Styles.Form>)
          }
          {(containsError || membershipError) && (
            <Styles.ErrorText data-testid="error-text" id="error-text">
              {formik.errors.document || membershipError}
            </Styles.ErrorText>
          )}
        </Fragment>
      )}
    </Styles.Content>
  );
};

export default IdentifyMember;

